// @givmo/app-charity — the per-module sign-in door (CPA-WEB40 · OM-12 mechanic 4).
//
// ══ ONE ROUTE, EVERY MOUNTED MODULE ════════════════════════════════════════════════════════════
//
// `/<module>/sign-in` — the segment is the module's PATH, not its slug, per the slug↔path table in
// `agent_docs/org-console-shell.md`. One parameterized route rather than one door per module: the
// only things that differ are the branding and where sign-in lands, and both are class-table data.
//
// ══ WHY THE VOCABULARY IS THE MOUNTED SET ══════════════════════════════════════════════════════
//
// The route validates its parameter against the modules this build actually serves (today: only
// `donations`), and an unknown segment falls back to the charity's ROOT door, `/login-home` — the
// guard's own target (§9-8) — never a branded door onto nothing. Validating against anything wider
// would put a Givmo page carrying a product's name in an environment where the product does not
// exist; the label and the glyph can only come from the class table.
//
// ══ WHERE IT LANDS ═════════════════════════════════════════════════════════════════════════════
//
// The module, not the landing rule. Someone who followed a link into Donations and was asked to sign
// in is owed the statement; recomputing where they generally belong would send an Owner to the
// organization's page instead. A `redirect` search param still overrides — through the ONE shared
// sanitizer — and the route's `beforeLoad` skips the door entirely for someone already signed in.

import { Icon } from '@givmo/design-system';
import { moduleForSegment } from '@givmo/org-console';
import { charityModuleDefinition, charityModuleRoutes } from '../../console/module-table';
import type { CharityModuleIcon } from '../../console/module-table';
import { AuthDoor } from './auth-door';
import { AUTH_ROUTES } from './auth-routes';

export interface ModuleDoor {
  /** Where signing in through this door lands. */
  path: string;
  label: string;
  icon: CharityModuleIcon;
}

/**
 * The mounted module a path segment belongs to, with the class table's own words for it — or
 * `undefined` when this build serves no such module. Exported because the ROUTE needs the same
 * answer before rendering anything: a door for a module that is not here must redirect, not paint.
 */
export function moduleDoorFor(segment: string): ModuleDoor | undefined {
  const route = moduleForSegment(segment, charityModuleRoutes());
  if (route?.path === undefined) return undefined;
  const definition = charityModuleDefinition(route.slug);
  return definition === undefined
    ? undefined
    : { path: route.path, label: definition.label, icon: definition.icon };
}

export interface ModuleSignInPageProps {
  /** The path segment from the address bar. */
  segment: string;
}

export function ModuleSignInPage({ segment }: ModuleSignInPageProps) {
  const door = moduleDoorFor(segment);
  // Unreachable: the route redirects an unknown segment to `/login-home` before this renders. Rendering
  // the charity's own door rather than throwing keeps a routing mistake from becoming a blank screen
  // on a sign-in address, and it is still an honest, working door.
  if (door === undefined) {
    return (
      <AuthDoor
        testId="page-login-home"
        emailTo={AUTH_ROUTES.signIn}
        emailTestId="auth-signin-email"
      />
    );
  }
  return (
    <AuthDoor
      testId="page-module-sign-in"
      emailTo={AUTH_ROUTES.signIn}
      emailTestId="auth-signin-email"
      defaultTarget={door.path}
      module={{ label: door.label, icon: <Icon icon={door.icon} size={20} aria-hidden /> }}
    />
  );
}
